// frontend/source/portal/components/AdminArtworkDetails.js
import React, { useState, useEffect } from "react";
import adminApi from "../../services/adminapi";
import MediumManagerModal from "./MediumManagerModal";
import "./AdminArtworkDetails.css";

const AdminArtworkDetails = ({
  artwork,
  currentIndex,
  totalCount,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(artwork.title);
  const [mediumId, setMediumId] = useState(artwork.mediumId?._id || "");
  const [date, setDate] = useState(
    artwork.date ? new Date(artwork.date).toISOString().split("T")[0] : "",
  );
  const [width, setWidth] = useState(artwork.width || "");
  const [height, setHeight] = useState(artwork.height || "");
  const [hidden, setHidden] = useState(artwork.hidden || false);
  const [available, setAvailable] = useState(artwork.available || false);
  const [mediums, setMediums] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showMediumManager, setShowMediumManager] = useState(false);

  useEffect(() => {
    loadMediums();
  }, []);

  useEffect(() => {
    // Reset form when artwork changes
    setTitle(artwork.title);
    setMediumId(artwork.mediumId?._id || "");
    setDate(
      artwork.date ? new Date(artwork.date).toISOString().split("T")[0] : "",
    );
    setWidth(artwork.width || "");
    setHeight(artwork.height || "");
    setHidden(artwork.hidden || false);
    setAvailable(artwork.available || false);
    setIsEditing(false);
  }, [artwork._id]);

  const loadMediums = async () => {
    try {
      const data = await adminApi.getMediums();
      setMediums(data);
    } catch (err) {
      console.error("Failed to load mediums:", err);
    }
  };

  const handleMediumsUpdated = () => {
    loadMediums();
  };

  const handleSave = async () => {
    if (title.trim() === "") {
      alert("Artwork title cannot be empty");
      return;
    }

    try {
      setIsSaving(true);
      const updateData = {
        title,
        hidden,
        available,
      };
      if (mediumId) updateData.mediumId = mediumId;
      if (date) updateData.date = date;
      if (width) updateData.width = parseFloat(width);
      if (height) updateData.height = parseFloat(height);

      await onUpdate(updateData);
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to save:", err);
      // Reset on error
      setTitle(artwork.title);
      setMediumId(artwork.mediumId?._id || "");
      setDate(
        artwork.date ? new Date(artwork.date).toISOString().split("T")[0] : "",
      );
      setWidth(artwork.width || "");
      setHeight(artwork.height || "");
      setHidden(artwork.hidden || false);
      setAvailable(artwork.available || false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTitle(artwork.title);
    setMediumId(artwork.mediumId?._id || "");
    setDate(
      artwork.date ? new Date(artwork.date).toISOString().split("T")[0] : "",
    );
    setWidth(artwork.width || "");
    setHeight(artwork.height || "");
    setHidden(artwork.hidden || false);
    setAvailable(artwork.available || false);
    setIsEditing(false);
  };

  // Get medium name for display
  const getMediumName = () => {
    if (typeof artwork.mediumId === "object" && artwork.mediumId?.name) {
      return artwork.mediumId.name;
    }
    return artwork.medium || "Not specified";
  };

  // Format dimensions for display
  const getDisplayDimensions = () => {
    if (artwork.width && artwork.height) {
      const unit = artwork.unit || "in";
      return `${artwork.width} × ${artwork.height} ${unit}`;
    }
    return "Not specified";
  };

  // Format date for display
  const getDisplayDate = () => {
    if (artwork.date) {
      return new Date(artwork.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
    return "Not specified";
  };

  return (
    <div className="admin-artwork-details">
      <div className="admin-artwork-details-panel">
        <div className="admin-artwork-details-header">
          {isEditing ? (
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="admin-artwork-details-title-input"
              placeholder="Artwork title"
              disabled={isSaving}
            />
          ) : (
            <h1 className="admin-artwork-details-title">{artwork.title}</h1>
          )}
          <p className="admin-artwork-details-counter">
            {currentIndex + 1} of {totalCount}
          </p>
        </div>

        <div className="admin-artwork-details-info">
          {/* Medium */}
          <div className="admin-artwork-details-item">
            <div className="admin-artwork-details-label-row">
              <h3 className="admin-artwork-details-label">Medium</h3>
              {isEditing && (
                <button
                  onClick={() => setShowMediumManager(true)}
                  className="admin-artwork-manage-link"
                  disabled={isSaving}
                >
                  Manage Mediums
                </button>
              )}
            </div>
            {isEditing ? (
              <select
                value={mediumId}
                onChange={(e) => setMediumId(e.target.value)}
                className="admin-artwork-details-select"
                disabled={isSaving}
              >
                <option value="">Select Medium</option>
                {mediums.map((medium) => (
                  <option key={medium._id} value={medium._id}>
                    {medium.name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="admin-artwork-details-value">{getMediumName()}</p>
            )}
          </div>

          {/* Dimensions */}
          <div className="admin-artwork-details-item">
            <h3 className="admin-artwork-details-label">Size</h3>
            {isEditing ? (
              <div className="admin-artwork-dimensions-inputs">
                <input
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  className="admin-artwork-details-input dimension-input"
                  placeholder="Width"
                  step="0.1"
                  min="0"
                  disabled={isSaving}
                />
                <span className="dimension-separator">×</span>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  className="admin-artwork-details-input dimension-input"
                  placeholder="Height"
                  step="0.1"
                  min="0"
                  disabled={isSaving}
                />
                <span className="dimension-unit">in</span>
              </div>
            ) : (
              <p className="admin-artwork-details-value">
                {getDisplayDimensions()}
              </p>
            )}
          </div>

          {/* Date */}
          <div className="admin-artwork-details-item">
            <h3 className="admin-artwork-details-label">Date</h3>
            {isEditing ? (
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="admin-artwork-details-input"
                disabled={isSaving}
              />
            ) : (
              <p className="admin-artwork-details-value">{getDisplayDate()}</p>
            )}
          </div>

          {/* Visibility & Availability Section */}
          <div className="admin-artwork-details-item admin-artwork-checkboxes-section">
            <h3 className="admin-artwork-details-label">Visibility & Status</h3>
            {isEditing ? (
              <div className="admin-artwork-checkboxes">
                <label className="admin-artwork-checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={hidden}
                    onChange={(e) => setHidden(e.target.checked)}
                    className="admin-artwork-checkbox-input"
                    disabled={isSaving}
                  />
                  <span className="admin-artwork-checkbox-custom"></span>
                  <span className="admin-artwork-checkbox-label">
                    <span className="admin-artwork-checkbox-label-text">
                      Hidden from public
                    </span>
                    <span className="admin-artwork-checkbox-description">
                      This artwork will not be visible to public viewers
                    </span>
                  </span>
                </label>

                <label className="admin-artwork-checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={available}
                    onChange={(e) => setAvailable(e.target.checked)}
                    className="admin-artwork-checkbox-input"
                    disabled={isSaving}
                  />
                  <span className="admin-artwork-checkbox-custom"></span>
                  <span className="admin-artwork-checkbox-label">
                    <span className="admin-artwork-checkbox-label-text">
                      Available for purchase
                    </span>
                    <span className="admin-artwork-checkbox-description">
                      Mark this artwork as available to buy
                    </span>
                  </span>
                </label>
              </div>
            ) : (
              <div className="admin-artwork-status-display">
                <div className="admin-artwork-status-badge-group">
                  <span
                    className={`admin-artwork-status-badge ${hidden ? "status-hidden" : "status-visible"}`}
                  >
                    {hidden ? "🔒 Hidden" : "👁️ Visible"}
                  </span>
                  <span
                    className={`admin-artwork-status-badge ${available ? "status-available" : "status-unavailable"}`}
                  >
                    {available ? "✓ Available" : "✕ Not Available"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="admin-artwork-details-actions">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="admin-artwork-details-btn save-btn"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={handleCancel}
                className="admin-artwork-details-btn cancel-btn"
                disabled={isSaving}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="admin-artwork-details-btn edit-btn"
            >
              Edit Details
            </button>
          )}
        </div>

        <div className="admin-artwork-details-footer">
          <p className="admin-artwork-details-hint">
            Use arrow keys or buttons to navigate between artworks
          </p>
        </div>
      </div>

      {showMediumManager && (
        <MediumManagerModal
          onClose={() => setShowMediumManager(false)}
          onMediumsUpdated={handleMediumsUpdated}
        />
      )}
    </div>
  );
};

export default AdminArtworkDetails;
